import React, { useCallback, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { useCombobox } from 'downshift';
import { VisuallyHidden } from '../../styled/accessibility';

import TextField from '../text-field';

import {
  Wrapper,
  SuggestionList,
  Suggestion,
  SuggestionHeading,
  SuggestionIcon,
  SuggestionWrapper,
} from './styles';
import iconSrc from '../../../utils/iconSrc';

const highlight = (suggestion, query) => {
  const index = suggestion.toLowerCase().indexOf(query.trim().toLowerCase());

  if (index !== -1) {
    const start = suggestion.slice(0, index);
    const highlighted = suggestion.slice(index, index + query.length);
    const end = suggestion.slice(index + query.length);

    return [start, <strong key="highlight">{highlighted}</strong>, end].filter(
      Boolean
    );
  }

  return suggestion;
};

const AutoSuggest = forwardRef(
  (
    {
      items,
      onInputValueChange,
      getA11yStatusMessage,
      onKeyDown,
      placeholder,
      labelText,
      suggestionHeading,
      suggestionPrefixIcon,
      ...rest
    },
    passedRef
  ) => {
    const {
      isOpen,
      inputValue,
      getMenuProps,
      getInputProps,
      highlightedIndex,
      getItemProps,
      getLabelProps,
    } = useCombobox({
      items,
      onInputValueChange,
      getA11yStatusMessage,
      inputId: rest.id,
    });

    const inputProps = getInputProps({
      onKeyDown,
    });
    const labelProps = getLabelProps();

    const {
      onChange: inputOnChange,
      onBlur: inputOnBlur,
      ref: inputRef,
    } = inputProps;

    // this let's downshift have a reference to the input while still letting other code
    // (formik f/e) have access to the reference

    const { onChange: restOnChange, onBlur: restOnBlur } = rest;

    const onChange = useCallback(
      (event) => {
        if (inputOnChange) inputOnChange(event);
        if (restOnChange) restOnChange(event);
      },

      [inputOnChange, restOnChange]
    );

    const onBlur = useCallback(
      (event) => {
        if (inputOnBlur) inputOnBlur(event);
        if (restOnBlur) restOnBlur(event);
      },
      [inputOnBlur, restOnBlur]
    );

    return (
      <Wrapper>
        <div>
          <VisuallyHidden
            as="label"
            htmlFor={labelProps.for}
            id={labelProps.id}
          >
            {labelText || placeholder}
          </VisuallyHidden>
          <VisuallyHidden id={`suggestion-desc-${rest.id}`}>
            ...when suggestion results are available use up and down arrows to
            navigate and press enter key to select.
          </VisuallyHidden>
          <TextField
            {...inputProps}
            {...rest}
            aria-expanded={isOpen && items.length > 0}
            aria-describedby={`suggestion-desc-${rest.id}`}
            onChange={onChange}
            onBlur={onBlur}
            placeholder={placeholder}
            value={inputValue || rest.value || ''}
            ref={(ref) => {
              inputRef(ref);
              if (typeof passedRef === 'function') {
                passedRef(ref);
              } else if (passedRef) {
                passedRef.current = ref;
              }
            }}
          />
        </div>
        <SuggestionWrapper $isOpen={isOpen && items.length > 0}>
          {suggestionHeading && (
            <SuggestionHeading>{suggestionHeading}</SuggestionHeading>
          )}
          <SuggestionList {...getMenuProps()}>
            {isOpen &&
              items.map((item, index) => (
                <Suggestion
                  key={`${item}${index}`}
                  {...getItemProps({ item, index })}
                  $highlighted={highlightedIndex === index}
                >
                  {suggestionPrefixIcon && (
                    <SuggestionIcon
                      src={iconSrc(suggestionPrefixIcon)}
                      cacheRequests
                      aria-hidden="true"
                    />
                  )}

                  {highlight(item, inputValue)}
                </Suggestion>
              ))}
          </SuggestionList>
        </SuggestionWrapper>
      </Wrapper>
    );
  }
);

AutoSuggest.displayName = 'AutoSuggest';

AutoSuggest.propTypes = {
  items: PropTypes.arrayOf(PropTypes.string),
  onInputValueChange: PropTypes.func,
  getA11yStatusMessage: PropTypes.func,
  onKeyDown: PropTypes.func,
  placeholder: PropTypes.string,
  labelText: PropTypes.string,
  suggestionHeading: PropTypes.string,
  suggestionPrefixIcon: PropTypes.string,
};

export default AutoSuggest;
